import { type JobEvent } from '@/features/jobs/JobEvent'
import { match } from '@/utils/pattern-match-util'

function strong(text: string | undefined): JobEventDescriptionPart {
  return { text: text ?? '', isStrong: true }
}

export interface JobEventDescriptionPart {
  text: string
  isStrong?: boolean
}

/**
 * Describes the event as a sentence, with the names it mentions emphasized.
 */
export function formatJobEventDescription(
  event: JobEvent,
): JobEventDescriptionPart[] {
  const actor = strong(event.triggeredBy.displayName)
  const actorDoes = (text: string) => [actor, { text: ` ${text}` }]

  return match<JobEvent['eventType'], JobEventDescriptionPart[]>(
    event.eventType,
    {
      Amended: () => actorDoes('amended the job'),
      Claimed: () => actorDoes('picked up the job'),
      Closed: () =>
        match(event.jobResolution ?? 'None', {
          Done: () => actorDoes('closed the job as Completed'),
          Cancelled: () => actorDoes('closed the job as Cancelled'),
          Test: () => actorDoes('closed the job as Test'),
          None: () => actorDoes('closed this job'),
        }),
      Forfeited: () => actorDoes('forfeited the job'),
      JobMemberAdded: () => [
        ...actorDoes('added '),
        strong(event.previousProvider),
        { text: ' to the job' },
      ],
      JobMemberRemoved: () => [
        ...actorDoes('removed '),
        strong(event.previousProvider),
        { text: ' from the job' },
      ],
      ProviderAdded: () => [
        ...actorDoes('added '),
        strong(event.newProvider),
        { text: ' to the job' },
      ],
      ProviderRemoved: () =>
        event.previousProvider
          ? [
              ...actorDoes('removed '),
              strong(event.previousProvider),
              { text: ' from the job' },
            ]
          : actorDoes(event.description),
      Reopened: () => actorDoes('reopened the job'),
      TaskMarkedComplete: () => [
        ...actorDoes('marked '),
        strong(`"${event.taskTitle}"`),
        { text: ' complete' },
      ],
      TaskMarkedIncomplete: () => [
        ...actorDoes('marked '),
        strong(`"${event.taskTitle}"`),
        { text: ' incomplete' },
      ],
      Transferred: () => {
        if (event.newTeam) {
          return [
            ...actorDoes('transferred the job to team '),
            strong(event.newTeam),
          ]
        }
        return [
          ...actorDoes('transferred the job to '),
          strong(event.newProvider || event.newClient),
        ]
      },
      Unclaimed: () => [{ text: `${event.description} by ` }, actor],
      Updated: () => actorDoes('updated the job'),
      Resumed: () => actorDoes('resumed the job'),
      ResumedJob: () => actorDoes('resumed the job'),
      PutOnHold: () => actorDoes('put the job on hold'),
      BecameInactive: () => [strong('The job'), { text: ' became inactive' }],
      PaymentInfoRequested: () => actorDoes('requested payment information'),
      PaymentInfoProvided: () => actorDoes('provided payment information'),
      PaymentSuccessful: () => actorDoes('has settled the balance'),
      PaymentFailed: () => [
        strong('Payment'),
        { text: ' failed for this job' },
      ],
      CouponAdded: () => [...actorDoes('added '), strong('a coupon')],
      CouponRemoved: () => [...actorDoes('removed '), strong('a coupon')],
      RefundSuccessful: () => actorDoes('refunded successfully'),
      RefundFailed: () => [strong('Attempt to refund'), { text: ' failed' }],
      ManualInvoiceAdded: () => actorDoes('added an invoice to the job'),
      AnswersCorrected: () => actorDoes('corrected the answers for the job'),
      InfoGatheringCompleted: () =>
        actorDoes('completed info gathering for the job'),
      ClientAdded: () => actorDoes('added a client to the job'),
      Unspecified: () => actorDoes(event.description),
      [match.DEFAULT]: () => actorDoes(event.description),
    },
  )
}

/**
 * E2E tests select events by the API's event type names.
 */
export function formatJobEventTestId(event: JobEvent) {
  return `job-event-${event.eventType.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase()}`
}
